import { Suspense, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useWindowSize } from 'src/shared/hooks/use-window-size'

import { useUserReviews } from '../../../api/marketplace/use-user-reviews'
import { ReviewStar } from '../../../assets/review-star'
import NoReviews from '../empty/no-reviews'
import SectionTitle from '../ui/section-title'
import ViewAllButton from '../ui/view-all-button'

import PublicPageReviewCard from './public-page-review-card'
import PublicPageReviewListSkeleton from './public-page-review-list-skeleton'

type ReviewListProps = {
  userId: number
  username: string
  onGoBack?: () => void
  showAll?: boolean
  mobile?: boolean
  firstName: string
  /** Used by the non public profile page to only show the review status not the title */
  onlyShowReviewStatus?: boolean
}

const ReviewListContent = ({
  userId,
  username,
  onGoBack,
  showAll = false,
  mobile,
  firstName,
  onlyShowReviewStatus = false,
}: ReviewListProps) => {
  const navigate = useNavigate()
  const { data } = useUserReviews(userId)
  const { width } = useWindowSize()
  const isMobile = width < 768

  const reviewStatus = useMemo(() => {
    return data?.totalReviews ? (
      <div className="flex items-center justify-center gap-1.5 text-xs font-semibold text-muted lg:gap-2 lg:text-[14px]">
        <ReviewStar size={13} pattern="gold" />
        <span>{(data?.averageRating || 0).toFixed(1)}</span>
        <span className="align-middle text-xs" style={{ fontSize: '10px', lineHeight: '1' }}>
          •
        </span>
        <span>
          {data?.totalReviews || 0} review{data?.totalReviews === 1 ? '' : 's'}
        </span>
      </div>
    ) : (
      <div className="text-sm font-semibold italic text-muted lg:text-base">No reviews</div>
    )
  }, [data])

  const reviews = useMemo(() => {
    return showAll ? data?.reviews || [] : data?.reviews?.slice(0, 4) || []
  }, [data, showAll])

  return (
    <section className="mb-6">
      {isMobile || onlyShowReviewStatus ? (
        <div className="mt-3">{reviewStatus}</div>
      ) : (
        <SectionTitle
          onGoBack={onGoBack}
          title={mobile ? reviewStatus : 'Reviews'}
          rightContent={mobile ? null : reviewStatus}
        />
      )}
      <div className="mt-4 grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-4">
        {reviews?.map((review) => (
          <PublicPageReviewCard key={`${review.serviceDescription}-${review.date}`} review={review} />
        ))}
      </div>
      {reviews?.length === 0 && <NoReviews firstName={firstName} />}
      {!showAll && reviews?.length > 0 && (
        <ViewAllButton
          text="Show all reviews"
          onClick={() => navigate(paths.users.publicProfile.reviews.getHref(username))}
        />
      )}
    </section>
  )
}

const ReviewList = ({
  userId,
  username,
  onGoBack,
  showAll = false,
  mobile,
  firstName,
  onlyShowReviewStatus = false,
}: ReviewListProps) => {
  return (
    <Suspense fallback={<PublicPageReviewListSkeleton />}>
      <ReviewListContent
        userId={userId}
        username={username}
        onGoBack={onGoBack}
        showAll={showAll}
        mobile={mobile}
        firstName={firstName}
        onlyShowReviewStatus={onlyShowReviewStatus}
      />
    </Suspense>
  )
}

export default ReviewList
